/**
 * setup.ts — how ready this tenant is to trade, and whether the app should hold them back.
 *
 * Read from `GET /api/onboarding/setup-state`, which answers the question with evidence rather
 * than row counts (see the long note on SETUP_STEP_META in the backend for why those are not
 * the same question, and what it cost to find out).
 *
 * ## Fail open, always
 *
 * Every failure mode here resolves to "not gated". A tenant must never be locked out of their
 * own application because a readiness endpoint was slow, a deploy was mid-flight, or a token
 * had just expired. The gate is a teaching aid for somebody who has never seen the product —
 * the cost of wrongly letting someone through is that they see an empty Devices page, and the
 * cost of wrongly keeping someone out is that they cannot run their ISP.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api/client'

export interface SetupStep {
  key: string
  label: string
  description: string
  route: string
  blocking: boolean
  complete: boolean
  detail?: string
}

export interface SetupState {
  siteId: string
  siteName: string
  createdVia: string
  approvalState: string
  approvalNote: string | null
  steps: SetupStep[]
  readyToSell: boolean
  gated: boolean
  exemptReason: string | null
}

export const useSetupStore = defineStore('setup', () => {
  const state = ref<SetupState | null>(null)
  const loading = ref(false)
  /** True once a fetch has resolved either way, so the guard knows not to wait again. */
  const loaded = ref(false)
  const loadedForSiteId = ref<string | null>(null)

  /**
   * The one flag the router guard reads.
   *
   * Defaults to false while unknown — see the fail-open note above. A `null` state is not
   * "gated until proven otherwise"; it is "we do not know, so get out of the way".
   */
  const gated = computed(() => state.value?.gated === true)
  const readyToSell = computed(() => state.value?.readyToSell !== false)
  const pendingApproval = computed(() => state.value?.approvalState === 'pending')

  const blockingSteps = computed(() => state.value?.steps.filter(s => s.blocking) ?? [])
  const nextStep = computed(() => blockingSteps.value.find(s => !s.complete) ?? null)
  const completedCount = computed(() => blockingSteps.value.filter(s => s.complete).length)
  const totalCount = computed(() => blockingSteps.value.length)
  /** The optional ones, shown separately so they never look like blockers. */
  const optionalSteps = computed(() => state.value?.steps.filter(s => !s.blocking) ?? [])

  async function fetch(siteId: string | null, force = false): Promise<void> {
    if (!siteId) {
      // No tenant selected — a super_admin on a platform screen, say. Nothing to gate.
      state.value = null
      loaded.value = true
      return
    }
    if (!force && loaded.value && loadedForSiteId.value === siteId) return

    loading.value = true
    try {
      const res = await api.get<{ data: SetupState }>('/onboarding/setup-state')
      state.value = res.data.data
      loadedForSiteId.value = siteId
    } catch {
      // Fail open. Deliberately silent: there is nothing the user could do about it, and a
      // red banner about a subsystem they have never heard of is worse than no banner.
      state.value = null
      loadedForSiteId.value = siteId
    } finally {
      loading.value = false
      loaded.value = true
    }
  }

  /** Call after any action that might have completed a step, so the UI does not lag reality. */
  async function refresh(siteId: string | null): Promise<void> {
    await fetch(siteId, true)
  }

  /** On site switch: the previous tenant's readiness says nothing about this one. */
  function clear(): void {
    state.value = null
    loaded.value = false
    loadedForSiteId.value = null
  }

  return {
    state, loading, loaded,
    gated, readyToSell, pendingApproval,
    blockingSteps, optionalSteps, nextStep, completedCount, totalCount,
    fetch, refresh, clear,
  }
})
